import { GitFork } from "lucide-react";

const AMBER = "oklch(var(--primary))";

const summary = [
  { label: "Trigger", value: "New invoice uploaded" },
  { label: "Version", value: "v2.3" },
  { label: "Last run", value: "14m ago" },
];

const dependencies = [
  "invoice-intake.workflow",
  "stripe-sync.workflow",
];

const outputs = [
  "Payables record updates",
  "Exception queue",
  "Evidence links",
];

const capabilities = [
  { label: "Type", value: "Verification" },
  { label: "Structured data", value: null },
  { label: "Exceptions", value: null },
  { label: "Notifications", value: null },
  { label: "Anonymization", value: "Off" },
];

const META_LABEL =
  "font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal";

const HEADER_BG = "oklch(0.94 0.006 75 / 0.45)";

const VersioningVisual = () => (
  <div className="rounded-2xl border border-border bg-surface-panel overflow-hidden select-none w-full text-sm">

    {/* Header */}
    <div
      className="px-5 py-4 border-b border-border flex items-start justify-between gap-4"
      style={{ background: HEADER_BG }}
    >
      <div className="min-w-0">
        <p className={`${META_LABEL} mb-1`}>Workflow version</p>
        <span className="font-serif font-semibold text-[0.95rem] tracking-[-0.01em] text-foreground">
          invoice-reconciliation.workflow
        </span>
        <p className="font-mono text-[0.6rem] text-muted-foreground mt-1">
          Published on 26.11.2025 10:45 PM
        </p>
      </div>
      <div className="shrink-0 pt-0.5">
        <button
          type="button"
          aria-label="Fork workflow"
          className="group flex items-center gap-1.5 rounded-full px-1.5 py-1 pr-2.5 font-mono text-[0.58rem] text-foreground transition-[transform,background-color,box-shadow] duration-500 active:scale-[0.98]"
          style={{
            background: "oklch(0.98 0.003 75)",
            boxShadow: "inset 0 0 0 1px oklch(0.80 0.014 75 / 0.8), 0 8px 18px -16px oklch(0.18 0.012 75 / 0.4)",
            transitionTimingFunction: "cubic-bezier(0.32,0.72,0,1)",
          }}
        >
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-px"
            style={{
              background: "oklch(0.94 0.006 75)",
              transitionTimingFunction: "cubic-bezier(0.32,0.72,0,1)",
            }}
          >
            <GitFork size={11} strokeWidth={1.55} className="text-muted-foreground" />
          </span>
          Fork
        </button>
      </div>
    </div>

    {/* Version summary */}
    <div className="px-5 py-4 border-b border-border">
      <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-2">
        Version summary
      </p>
      <p className="text-[0.78rem] leading-[1.5] text-muted-foreground max-w-[62ch] mb-3">
        This workflow applies checks on entities extracted from invoices against the Stripe API. The Stripe integration requires configuration before the workflow can run.
      </p>
      <div className="overflow-x-auto">
        <div
          data-testid="versioning-summary-surface"
          className="grid min-w-[420px] divide-x divide-border rounded-xl border border-border overflow-hidden"
          style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
        >
          {summary.map(({ label, value }) => (
            <div key={label} className="px-3.5 py-3">
              <p className={`${META_LABEL} mb-1.5`}>{label}</p>
              <p
                className="font-mono text-[0.72rem] leading-tight"
                style={{ color: label === "Version" ? AMBER : "oklch(0.12 0.008 75)" }}
              >
                {value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Workflow definition */}
    <div
      role="region"
      aria-label="Invoice reconciliation workflow definition"
      className="px-5 py-4"
    >
      <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-3">
        Workflow definition
      </p>
      <div
        data-testid="versioning-definition-surface"
        className="overflow-x-auto rounded-xl border border-border bg-surface-panel overflow-hidden"
      >
        <div className="min-w-[560px]">

          {/* Column headers */}
          <div
            className="grid border-b border-border"
            style={{
              background: HEADER_BG,
              gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.3fr)",
            }}
          >
            <h4 className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
              Dependencies
            </h4>
            <h4 className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
              Outputs
            </h4>
            <h4 className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
              Capabilities
            </h4>
          </div>

          {/* Column content */}
          <div
            className="grid divide-x divide-border"
            style={{ gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.3fr)" }}
          >
            {/* Dependencies — amber chips with ↳ prefix */}
            <div className="px-3.5 py-3 space-y-1.5">
              {dependencies.map((dep) => (
                <div key={dep} className="flex items-center gap-1.5">
                  <span className="font-mono text-[0.62rem] shrink-0" style={{ color: AMBER }}>↳</span>
                  <span
                    className="font-mono text-[0.6rem] px-1.5 py-0.5 rounded border leading-none"
                    style={{
                      color: AMBER,
                      borderColor: "oklch(var(--primary) / 0.3)",
                      background: "oklch(var(--primary) / 0.06)",
                    }}
                  >
                    {dep}
                  </span>
                </div>
              ))}
            </div>

            {/* Outputs — amber → prefix */}
            <div className="px-3.5 py-3 space-y-1.5">
              {outputs.map((item) => (
                <div key={item} className="flex items-baseline gap-1.5">
                  <span className="font-mono text-[0.62rem] shrink-0" style={{ color: AMBER }}>→</span>
                  <span className="font-mono text-[0.6rem] text-foreground leading-[1.35]">{item}</span>
                </div>
              ))}
            </div>

            {/* Capabilities — plain list */}
            <div className="px-3.5 py-3 space-y-1.5">
              {capabilities.map(({ label, value }) => (
                <div key={label} className="flex items-baseline gap-1.5">
                  <span
                    className="font-mono text-[0.6rem] leading-[1.35]"
                    style={{ color: label === "Anonymization" ? "oklch(0.55 0.012 75)" : "oklch(0.12 0.008 75)" }}
                  >
                    {label}
                  </span>
                  {value && (
                    <span className="font-mono text-[0.58rem] text-muted-foreground leading-[1.35]">
                      · {value}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
);

export default VersioningVisual;
